import { useCallback } from "react";

import { useStableRef } from "../../hooks/useStableRef.js";
import { useSelector, useStore } from "../../redux/hooks";
import {
  GridLayoutMP,
  hexVersionMPSelectors,
} from "../../redux/slices/hexVersionMPSlice";
import { useProjectContext } from "../../util/projectContext";

export interface UseGridLayoutsSelectorArgs<T> {
  selector: (gridLayouts: GridLayoutMP[]) => T;
  equalityFn?: (left: T, right: T) => boolean;
}

export function useGridLayoutsSelector<T>({
  equalityFn,
  selector,
}: UseGridLayoutsSelectorArgs<T>): T {
  const { hexVersionId } = useProjectContext();

  return useSelector((state) => {
    const gridLayoutsState = hexVersionMPSelectors
      .getGridLayoutSelectors(hexVersionId)
      .selectAll(state);

    if (gridLayoutsState == null) {
      throw new Error(
        `Missing grid layouts state for hex version: ${hexVersionId}`,
      );
    }

    return selector(gridLayoutsState);
  }, equalityFn);
}

export interface UseGridLayoutsGetterArgs<TArgs extends unknown[], TResult> {
  selector?: (gridLayouts: GridLayoutMP[], ...args: TArgs) => TResult;
}

export type UseGridLayoutsGetterResult<TArgs extends unknown[], TResult> = (
  ...args: TArgs
) => TResult;

export function useGridLayoutsGetter<
  TArgs extends unknown[],
  TResult = GridLayoutMP[],
>({
  selector,
}: UseGridLayoutsGetterArgs<TArgs, TResult> = {}): UseGridLayoutsGetterResult<
  TArgs,
  TResult
> {
  const { hexVersionId } = useProjectContext();
  const store = useStore();
  const selectorRef = useStableRef(selector);

  return useCallback(
    (...args: TArgs) => {
      const gridLayouts = hexVersionMPSelectors
        .getGridLayoutSelectors(hexVersionId)
        .selectAll(store.getState());

      if (gridLayouts == null) {
        throw new Error(
          `Missing grid layouts state for hex version: ${hexVersionId}`,
        );
      }

      // this cannot be conditionally chained/nullish coalesced since
      // the provided selector may intentionally return undefined
      return selectorRef.current
        ? selectorRef.current(gridLayouts, ...args)
        : (gridLayouts as unknown as TResult);
    },
    [hexVersionId, store, selectorRef],
  );
}
